"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Send,
  SquarePen
} from "lucide-react";
import { RequireRole } from "../console-shell";
import { Banner, Button, Desk, StatusTag } from ".";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { api, commandId, formatTime, reasonText, type AttachmentSummary } from "../../lib/api";
import { useSession } from "../../lib/session";
import {
  CONFIG,
  STATUS_LABEL,
  displayStatus,
  statusLabel,
  flattenOrgs,
  nextStep,
  statusTone,
  type ContentKind,
  type ContentRecord,
  type ContentStatus,
  type OrgNode
} from "./content-model";
import { ContentConfirmedCommand } from "./content-confirmed-command";
import { listPortalReceipts, type ChannelReceipt, screenChannelPath, portalError } from "../../lib/portal-pipeline-api";
import { ContentEditor } from "./content-editor";
import { ContentPreview } from "./content-preview";
import { ConfirmedCommand } from "./confirmed-command";
import { PortalChannelsPanel } from "./portal-channels";
import { PortalMetrics } from "./portal-metrics";
import { ContentAttachments } from "./content-attachments";

const names: Record<string, string> = { PORTAL: '公共门户', MINI_GUEST: '小程序游客页', SCREEN: '公众展屏', WECHAT_MP: '微信公众号' };
function PortalScreenReceipts({ record, disabled, onSaved }: { record: ContentRecord; disabled: boolean; onSaved: (next: ContentRecord) => void }) {
  const id = record.id;
  const { hasRole } = useSession();
  const [items, setItems] = useState<ChannelReceipt[] | null>(null), [error, setError] = useState(''), [reload, setReload] = useState(0);
  useEffect(() => { let current = true; void listPortalReceipts(id).then(r => { if (current) setItems(r.items); }).catch(() => { if (current) { setItems(null); setError('渠道回执读取失败。'); } }); return () => { current = false; }; }, [id, record.version, reload]);
  return <section className="content-metadata"><h3>渠道回执</h3>{error && <p role="alert">{error}<Button size="sm" variant="outline" onClick={() => { setError(''); setReload(n => n + 1); }}>重新读取回执</Button></p>}{items === null && !error && <p>正在读取回执…</p>}<dl>{Object.entries(names).map(([code, label]) => {
    const row = items?.filter(r => r.channel === code).sort((a, b) => b.sequence - a.sequence)[0];
    return <div key={code}><dt>{label}</dt><dd>{row ? row.receiptKind === 'DISABLED_XXX' ? '未启用 · XXX-CHANNEL-001' : row.status === 'WITHDRAWN' ? '已撤回' : row.status === 'RELEASED' ? row.releasedAt && Date.parse(row.releasedAt) > Date.now() ? '已准备 · 等待定时公开' : '已公开' : '处理失败' : '尚无回执'}{row?.releasedAt && <p>公开时间：{formatTime(row.releasedAt)}</p>}{row?.receiptRef && <details><summary>查看回执依据</summary><p>{row.receiptRef}</p></details>}{code === "SCREEN" && record.status === "PUBLISHED" && hasRole("CHANNEL_PUBLISHER") && items !== null && !error && (row?.status === "RELEASED" ? <ConfirmedCommand<ContentRecord> reauthenticate label="撤回展屏" title="撤回这篇内容的展屏渠道？" consequence="展屏停止显示；门户公开内容与历史回执继续保留。需要重新验证身份。" path={screenChannelPath(id, "withdraw")} body={{ expectedVersion: record.version }} reasonLabel="撤回展屏理由" reasonKey="note" disabled={disabled} onSaved={onSaved} /> : <ContentConfirmedCommand<ContentRecord> reauthenticate label="放行至展屏" title="单独放行至公众展屏？" consequence="这篇已公开内容将进入公众展屏。请确认公开范围，并重新验证身份。" path={screenChannelPath(id, "release")} body={{ expectedVersion: record.version }} disabled={disabled} onSaved={onSaved} />)}</dd></div>;
  })}</dl></section>;
}

export function ContentWorkspace({ kind }: { kind: ContentKind }) {
  const { hasRole } = useSession();
  if (kind === "portal" && hasRole("ORGANIZATION_ADMIN") && !hasRole("CHANNEL_PUBLISHER") && !hasRole("CONTENT_AUTHOR")) return <Desk><PortalChannelsPanel /></Desk>;
  if (kind === "portal" && hasRole("CHANNEL_PUBLISHER")) return <Suspense fallback={<Desk>正在读取工作台…</Desk>}><ContentDesk kind={kind} /></Suspense>;
  return (
    <RequireRole roleCode="CONTENT_AUTHOR">
      <Suspense
        fallback={
          <Desk>
            <p role="status">正在读取工作台…</p>
          </Desk>
        }
      >
        <ContentDesk kind={kind} />
      </Suspense>
    </RequireRole>
  );
}
function ContentDesk({ kind }: { kind: ContentKind }) {
  const config = CONFIG[kind];
  const portal = kind === "portal";
  const { whoami, hasRole } = useSession();
  const canAuthor = hasRole("CONTENT_AUTHOR");
  const params = useSearchParams();
  const [filter, setFilter] = useState<ContentStatus | "">("DRAFT");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<ContentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [readAt, setReadAt] = useState("");
  const [orgs, setOrgs] = useState<OrgNode[]>([]);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ContentRecord | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ record: ContentRecord | null } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [checked, setChecked] = useState([false, false]);
  // 当前详情的附件行，由附件区读到后交上来；预览据此画出封面、正文后插图与公开附件目录。
  const [files, setFiles] = useState<AttachmentSummary[] | null>(null);
  const listRequest = useRef(0),
    detailRequest = useRef(0),
    orgRequest = useRef(0);
  const busy = useRef(false);
  const command = useRef<{ fingerprint: string; id: string } | null>(null);
  const focus = useRef<HTMLElement | null>(null);
  const newButton = useRef<HTMLButtonElement>(null);
  const load = useCallback(async () => {
    const ticket = ++listRequest.current;
    setLoading(true);
    setListError(null);
    try {
      const result = await api<{ items: ContentRecord[] }>(
        `${config.endpoint}${filter ? `?status=${filter}` : ""}`
      );
      if (ticket === listRequest.current) {
        setItems(result.items);
        setReadAt(new Date().toISOString());
      }
    } catch (error) {
      if (ticket === listRequest.current) {
        setItems([]);
        setListError(reasonText(error));
      }
    } finally {
      if (ticket === listRequest.current) setLoading(false);
    }
  }, [config.endpoint, filter]);
  const loadOrgs = useCallback(async () => {
    const ticket = ++orgRequest.current;
    setOrgError(null);
    if (!canAuthor) { setOrgs([]); return; }
    try {
      const result = await api<{ items: OrgNode[] }>("/api/v1/orgs/authoring-options");
      if (ticket === orgRequest.current) setOrgs(result.items);
    } catch (error) {
      if (ticket === orgRequest.current) {
        setOrgs([]);
        setOrgError(reasonText(error));
      }
    }
  }, [canAuthor]);
  useEffect(() => {
    void load();
    return () => {
      listRequest.current++;
    };
  }, [load]);
  useEffect(() => {
    void loadOrgs();
    return () => {
      orgRequest.current++;
    };
  }, [loadOrgs]);
  const open = useCallback(
    async (id: string) => {
      const ticket = ++detailRequest.current;
      setDetailId(id);
      setDetail(null);
      setFiles(null);
      setDetailLoading(true);
      setDetailError(null);
      setFailure(null);
      setReviewing(false);
      setChecked([false, false]);
      try {
        const result = await api<ContentRecord>(`${config.endpoint}/${encodeURIComponent(id)}`);
        if (ticket === detailRequest.current) setDetail(result);
      } catch (error) {
        if (ticket === detailRequest.current) setDetailError(reasonText(error));
      } finally {
        if (ticket === detailRequest.current) setDetailLoading(false);
      }
    },
    [config.endpoint]
  );
  // 附件变动后重读详情：封面由服务端按公开规则给出，这里不自己猜；不清空现有详情，不打断正在看的页面。
  const refreshDetail = useCallback(
    async (id: string) => {
      const ticket = detailRequest.current;
      try {
        const result = await api<ContentRecord>(`${config.endpoint}/${encodeURIComponent(id)}`);
        if (ticket === detailRequest.current) setDetail(result);
      } catch {
        /* 保留现有详情；附件列表本身已经刷新 */
      }
    },
    [config.endpoint]
  );
  useEffect(() => {
    const record = params.get("record");
    if (record) void open(record);
    return () => {
      detailRequest.current++;
    };
  }, [params, open]);
  const close = () => {
    if (busy.current || uploading) return;
    detailRequest.current++;
    setDetailId(null);
    setReviewing(false);
    setFailure(null);
  };
  const mutate = async (path: string, body: Record<string, unknown>) => {
    const fingerprint = JSON.stringify({ path, body });
    if (command.current?.fingerprint !== fingerprint)
      command.current = { fingerprint, id: commandId() };
    return api<ContentRecord>(path, {
      method: "POST",
      body: { ...body, commandId: command.current.id }
    });
  };
  const save = async (body: Record<string, unknown>) => {
    if (busy.current) return;
    busy.current = true;
    setWorking(true);
    setFailure(null);
    setMessage("");
    try {
      const record = editor?.record;
      const result = await mutate(
        record
          ? `${config.endpoint}/${encodeURIComponent(record.id)}/${config.update}`
          : config.endpoint,
        body
      );
      command.current = null;
      setEditor(null);
      setDetail(result);
      setDetailId(result.id);
      setReviewing(false);
      setChecked([false, false]);
      setMessage(
        record
          ? "草稿修改已保存。核对内容后可继续送审。"
          : "已存为草稿，尚未发布。核对内容后可继续送审。"
      );
      void load();
    } catch (error) {
      setFailure(portal && error instanceof Error && "reasonCode" in error && ["SUBJECT_CONSENT_REQUIRED", "CHANNEL_DISABLED", "NOT_ACTIVATED"].includes(String(error.reasonCode)) ? portalError(error) : reasonText(error));
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };
  const submit = async () => {
    if (!detail || busy.current || uploading || !checked.every(Boolean)) return;
    busy.current = true;
    setWorking(true);
    setFailure(null);
    setMessage("");
    try {
      const result = await mutate(
        `${config.endpoint}/${encodeURIComponent(detail.id)}/${config.submit}`,
        { expectedVersion: detail.version }
      );
      command.current = null;
      setDetail(result);
      setReviewing(false);
      setChecked([false, false]);
      setMessage(
        portal
          ? "已送保密与公开性审查，等待审查人办理。"
          : "已送审，等待另一位同事放行。通知尚未发布。"
      );
      void load();
    } catch (error) {
      setFailure(portal && error instanceof Error && "reasonCode" in error && ["SUBJECT_CONSENT_REQUIRED", "CHANNEL_DISABLED", "NOT_ACTIVATED"].includes(String(error.reasonCode)) ? portalError(error) : reasonText(error));
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };
  const startEditor = (record: ContentRecord | null) => {
    detailRequest.current++;
    setDetailId(null);
    setDetailLoading(false);
    setDetailError(null);
    setReviewing(false);
    setFailure(null);
    setMessage("");
    setEditor({ record });
  };
  const visible = items.filter((row) =>
    `${row.title} ${row.organization.name} ${row.category ?? row.channelTitle ?? ""} ${row.publicRef ?? ""}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase())
  );
  const canEdit =
    hasRole("CONTENT_AUTHOR") && detail?.status === "DRAFT" && (portal || detail.authoredBy.accountId === whoami?.accountId);
  const names = (ids: string[]) =>
    ids
      .map(
        (id) =>
          flattenOrgs(orgs).find((node) => node.id === id)?.name ??
          (detail?.organization.id === id ? detail.organization.name : "组织名称暂不可读")
      )
      .join("、");
  const stages = portal ? ["起草", "保密审查", "放行", "公开"] : ["起草", "待放行", detail && displayStatus(detail) === "WITHDRAWN" ? "已撤回" : "已发布"];
  const stage =
    detail?.status === "DRAFT"
      ? 0
      : detail?.status === "PENDING_PRIVACY_REVIEW"
        ? 1
        : detail?.status === "PENDING_RELEASE"
          ? portal
            ? 2
            : 1
          : detail?.status === "PUBLISHED" || detail?.status === "RELEASED"
            ? stages.length - 1
            : -1;
  return (
    <Desk wide>
      <div className="workspace content-workspace">
        {editor ? (
          <ContentEditor
            key={editor.record?.id ?? "new"}
            kind={kind}
            record={editor.record}
            orgs={orgs}
            orgError={orgError}
            reloadOrgs={() => void loadOrgs()}
            whoami={whoami}
            working={working}
            failure={failure}
            onSave={save}
            onReload={() => {
              if (!editor.record) return;
              const id = editor.record.id;
              setEditor(null);
              void open(id);
            }}
            onLeave={() => {
              setEditor(null);
              setFailure(null);
              requestAnimationFrame(() => newButton.current?.focus());
            }}
          />
        ) : (
          <>
            <div className="workspace-heading">
              <div>
                <div className="workspace-eyebrow">
                  {portal ? "公开门户 · 内容工作台" : "通知与会务 · 编发工作台"}
                </div>
                <h1 className="workspace-title">{config.title}</h1>
                <p className="workspace-subtitle">{config.description}</p>
              </div>
              {hasRole("CONTENT_AUTHOR") && <Button ref={newButton} onClick={() => startEditor(null)}>
                <Plus size={16} />
                新建{config.noun}
              </Button>}
            </div>
            {portal && <PortalMetrics />}
            {message && (
              <div role="status">
                <Banner tone="success">{message}</Banner>
              </div>
            )}
            <div className="workspace-tabs" aria-label={`${config.title}状态`}>
              {["", ...config.statuses].map((status) => (
                <button
                  key={status}
                  type="button"
                  aria-pressed={filter === status}
                  onClick={() => setFilter(status as ContentStatus | "")}
                >
                  {status ? STATUS_LABEL[status as ContentStatus] : "全部"}
                </button>
              ))}
            </div>
            <div className="workspace-toolbar">
              <label className="workspace-search">
                <Search />
                <span className="sr-only">搜索{config.noun}</span>
                <input
                  className="content-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索标题、组织或类别"
                />
              </label>
              <div className="workspace-actions">
                <span className="workspace-count">
                  {loading
                    ? "正在读取…"
                    : listError
                      ? "读取失败"
                      : `本次 ${items.length} 条${query ? ` · 匹配 ${visible.length} 条` : ""}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="刷新列表"
                  disabled={loading}
                  onClick={() => void load()}
                >
                  <RefreshCw size={15} />
                </Button>
              </div>
            </div>
            {loading ? (
              <div className="workspace-loading" role="status" aria-label="正在读取列表">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} />
                ))}
              </div>
            ) : listError ? (
              <div className="workspace-error" role="alert">
                {listError}
                <Button variant="outline" onClick={() => void load()}>
                  重新读取
                </Button>
              </div>
            ) : !visible.length ? (
              <div className="workspace-empty">
                <FileText />
                <strong>{query ? "没有匹配的内容" : "当前状态下暂无内容"}</strong>
                <p>
                  {query
                    ? "搜索只覆盖本次读取的记录，可以换个关键词。"
                    : "可以切换状态，或开始起草一份新内容。"}
                </p>
                {hasRole("CONTENT_AUTHOR") && <Button
                  variant="outline"
                  onClick={() => (query ? setQuery("") : startEditor(null))}
                >
                  {query ? "清除搜索" : `新建${config.noun}`}
                </Button>}
              </div>
            ) : (
              <div className="activity-table-wrap">
                <table className="activity-table content-table">
                  <caption className="sr-only">{config.title}列表</caption>
                  <thead>
                    <tr>
                      <th scope="col">{portal ? "稿件" : "通知"}</th>
                      <th scope="col">状态</th>
                      <th scope="col">{portal ? "承办组织" : "接收范围"}</th>
                      <th scope="col">最近更新</th>
                      <th scope="col">
                        <span className="sr-only">操作</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <button
                            className="activity-record"
                            onClick={(event) => {
                              focus.current = event.currentTarget;
                              void open(row.id);
                            }}
                          >
                            <span className="activity-record__icon">
                              <FileText size={16} />
                            </span>
                            <span>
                              {row.title}
                              <span className="activity-record__meta">
                                {portal
                                  ? `${row.channelTitle}${row.genre ? ` · ${row.genre}` : ""}`
                                  : `${row.category} · ${row.organization.name}`}
                              </span>
                            </span>
                          </button>
                        </td>
                        <td>
                          <StatusTag tone={statusTone(displayStatus(row))}>
                            {statusLabel(kind, row)}
                          </StatusTag>
                        </td>
                        <td>
                          {portal
                            ? row.organization.name
                            : `${row.status === "RELEASED" ? "发布时" : "预估"} ${row.audienceCount ?? 0} 人`}
                          <span className="activity-table__sub">
                            {portal
                              ? row.authoredBy.displayName
                              : row.requiresAcknowledgement
                                ? "需确认知悉"
                                : "无需确认知悉"}
                          </span>
                        </td>
                        <td>
                          <time dateTime={row.updatedAt}>{formatTime(row.updatedAt)}</time>
                        </td>
                        <td>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`查看${row.title}`}
                            onClick={(event) => {
                              focus.current = event.currentTarget;
                              void open(row.id);
                            }}
                          >
                            <ArrowRight size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="home-source-note">
              仅显示授权范围内当前状态最近更新的最多 100 条记录；搜索限本次读取。
              {readAt && !loading && !listError ? `读取于 ${formatTime(readAt)}。` : ""}
            </p>
            <div className="content-workflow-note">
              <FileText size={18} />
              <div>
                <strong>
                  {portal ? "起草 → 保密与公开性审查 → 放行公开" : "起草 → 核对送审 → 放行发布"}
                </strong>
                <p>
                  {portal
                    ? "起草、审查与放行由不同人员承担，审查和放行在手机端完成。"
                    : "通知送审后，由另一位有放行职责的同事在手机端确认发布。"}
                </p>
              </div>
            </div>
          </>
        )}
        {portal && hasRole("CHANNEL_PUBLISHER") && <PortalChannelsPanel />}
        <Sheet
          open={detailId !== null && !editor}
          onOpenChange={(value) => {
            if (!value) close();
          }}
        >
          <SheetContent
            className="activity-sheet content-sheet workspace"
            showCloseButton={!working && !uploading}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              if (!editor)
                (focus.current?.isConnected ? focus.current : newButton.current)?.focus();
            }}
            onEscapeKeyDown={(event) => {
              if (working || uploading) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (working || uploading) event.preventDefault();
            }}
          >
            <SheetHeader>
              <SheetTitle>{reviewing ? "送审前核对" : `${config.noun}详情`}</SheetTitle>
              <SheetDescription>
                {detail
                  ? `${detail.authoredBy.displayName} · 更新于 ${formatTime(detail.updatedAt)} · 版本 ${detail.version}`
                  : "读取正文、范围与办理记录"}
              </SheetDescription>
            </SheetHeader>
            <div className="activity-sheet__body">
              {detailLoading && <p role="status">正在读取详情…</p>}
              {detailError && (
                <div className="workspace-error" role="alert">
                  {detailError}
                  <Button variant="outline" onClick={() => detailId && void open(detailId)}>
                    重新读取详情
                  </Button>
                </div>
              )}
              {detail && (
                <>
                  <div className="content-detail-head">
                    <StatusTag tone={statusTone(displayStatus(detail))}>
                      {statusLabel(kind, detail)}
                    </StatusTag>
                    <span>{detail.organization.name}</span>
                    {detail.publicRef && <span>{detail.publicRef}</span>}
                  </div>
                  <ol className="content-progress" aria-label="办理阶段">
                    {stages.map((label, i) => (
                      <li
                        key={label}
                        data-complete={stage > i}
                        aria-current={stage === i ? "step" : undefined}
                      >
                        <span>{stage > i ? <Check size={13} /> : i + 1}</span>
                        {label}
                      </li>
                    ))}
                  </ol>
                  <div className="content-next-step">
                    <strong>{reviewing ? "确认送审的内容与范围" : "下一步"}</strong>
                    <p>{nextStep(kind, displayStatus(detail))}</p>
                    {detail.withdrawnReason && <p>撤下原因：{detail.withdrawnReason}</p>}
                  </div>
                  <ContentPreview kind={kind} record={detail} files={portal ? files : undefined} />
                  {portal && <><section className="content-metadata"><h3>版本与纠错</h3><p>内容版本：{detail.revisionNo ?? 1}</p>{detail.correctsContentId && <p>本文为纠错版本，原稿与审放记录继续保留。</p>}{detail.supersededByContentId && <p>本文已被纠错版本取代。</p>}{detail.scheduledPublishAt && <p>计划公开：{formatTime(detail.scheduledPublishAt)}</p>}{hasRole("CONTENT_AUTHOR") && detail.status === "PUBLISHED" && <ContentConfirmedCommand<ContentRecord> label="生成纠错版本" title="生成这篇内容的纠错版本？" consequence="生成新草稿后须重新经过保密审查与放行；新版本公开时取代原稿。" path={`/api/v1/portal-contents/${detail.id}/correct`} body={{ expectedVersion: detail.version }} disabled={working || uploading} onSaved={next => { setDetail(next); setDetailId(next.id); setMessage("纠错草稿已生成，请核对并重新送审。"); void load(); }} />}</section><PortalScreenReceipts key={`${detail.id}-${detail.version}`} record={detail} disabled={working || uploading} onSaved={next => { setDetail(next); setMessage("展屏渠道回执已更新。"); void load(); }} /></>}
                  <section className="content-metadata">
                    <h3>{portal ? "公开设置" : "接收范围与来源"}</h3>
                    <dl>
                      {portal ? (
                        <>
                          <div>
                            <dt>展示日期</dt>
                            <dd>{detail.displayDate}（展示日期）</dd>
                          </div>
                          <div>
                            <dt>公开来源</dt>
                            <dd>{detail.publicSource || "未填写"}</dd>
                          </div>
                          <div>
                            <dt>封面</dt>
                            <dd>
                              {detail.cover
                                ? `${detail.cover.name}（公开后作首图与列表缩略图）`
                                : "无图片附件，公开后版面不带图"}
                            </dd>
                          </div>
                          {detail.privacyReviewedBy && (
                            <div>
                              <dt>保密审查</dt>
                              <dd>{detail.privacyReviewedBy.displayName}</dd>
                            </div>
                          )}
                          {detail.releasedBy && (
                            <div>
                              <dt>放行人</dt>
                              <dd>{detail.releasedBy.displayName}</dd>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>
                            <dt>接收方式</dt>
                            <dd>
                              {detail.audienceMode === "ORGANIZATION_SUBTREE"
                                ? "所选组织及其下级"
                                : "仅所选组织本级"}
                            </dd>
                          </div>
                          <div>
                            <dt>接收组织</dt>
                            <dd>{names(detail.audienceOrganizationIds ?? []) || "未配置"}</dd>
                          </div>
                          <div>
                            <dt>{detail.status === "RELEASED" ? "发布时人数" : "当前预估"}</dt>
                            <dd>
                              {detail.audienceCount ?? 0} 人
                              {detail.status !== "RELEASED" && "，以放行时确定的名单为准"}
                            </dd>
                          </div>
                          <div>
                            <dt>来源依据</dt>
                            <dd>
                              {detail.sourceType} · {detail.sourceRef}
                            </dd>
                          </div>
                        </>
                      )}
                    </dl>
                  </section>
                  {portal && (
                    <ContentAttachments
                      key={detail.id}
                      ownerId={detail.id}
                      editable={hasRole("CONTENT_AUTHOR") && detail.status === "DRAFT" && !reviewing}
                      disabled={working || reviewing}
                      onBusy={setUploading}
                      cover={detail.cover ?? null}
                      onRows={setFiles}
                      onChanged={() => void refreshDetail(detail.id)}
                    />
                  )}
                  {!portal && hasRole("CONTENT_AUTHOR") && detail.authoredBy.accountId === whoami?.accountId && ["DRAFT", "PENDING_RELEASE"].includes(detail.status) && !reviewing && <ConfirmedCommand<ContentRecord> label="作废草稿" title="作废这份通知草稿？" consequence="作废后不能继续送审或发布，原稿与办理记录保留。" path={`${config.endpoint}/${detail.id}/discard`} body={{ expectedVersion: detail.version }} reasonLabel="作废理由" reasonKey="note" minReason={4} disabled={working || uploading} onSaved={(next) => { setDetail(next); setMessage("通知草稿已作废。"); void load(); }} />}
                  {reviewing ? (
                    <section className="content-review-checks" aria-label="送审核对项">
                      <h3>请完成核对</h3>
                      {[
                        "已核对标题、正文和来源",
                        portal
                          ? "已检查拟公开的正文、封面图片和附件中的个人信息、企业信息和内部材料"
                          : "已核对接收组织、范围方式及知悉要求"
                      ].map((label, i) => (
                        <label key={label} className="content-check">
                          <Checkbox
                            checked={checked[i]}
                            disabled={working}
                            onCheckedChange={(value) =>
                              setChecked((prev) =>
                                prev.map((v, j) => (i === j ? value === true : v))
                              )
                            }
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                      {!portal && detail.audienceCount === 0 && (
                        <p className="content-field-note">
                          当前预估接收人数为 0，请先核对接收范围。
                        </p>
                      )}
                    </section>
                  ) : (
                    <section className="content-timeline">
                      <h3>办理记录</h3>
                      {detail.timeline?.length ? (
                        <ol>
                          {detail.timeline.map((event) => (
                            <li key={event.sequence}>
                              <div>
                                <strong>
                                  {event.fromStatus === event.toStatus
                                    ? "修改草稿"
                                    : event.fromStatus && event.toStatus === "DRAFT"
                                      ? "退回起草"
                                      : STATUS_LABEL[event.toStatus]}
                                </strong>
                                <time dateTime={event.occurredAt}>
                                  {formatTime(event.occurredAt)}
                                </time>
                              </div>
                              {event.note && <p>{event.note}</p>}
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="content-field-note">暂无办理记录。</p>
                      )}
                    </section>
                  )}
                </>
              )}
              {failure && (
                <div className="workspace-error" role="alert">
                  {failure}
                  <Button
                    variant="outline"
                    disabled={working}
                    onClick={() => detailId && void open(detailId)}
                  >
                    重新读取当前稿件
                  </Button>
                </div>
              )}
            </div>
            {detail && (
              <footer className="content-sheet__footer">
                {reviewing ? (
                  <>
                    <Button
                      variant="ghost"
                      disabled={working}
                      onClick={() => {
                        setReviewing(false);
                        setChecked([false, false]);
                        setFailure(null);
                      }}
                    >
                      <ArrowLeft size={15} />
                      返回详情
                    </Button>
                    <Button
                      disabled={
                        working ||
                        uploading ||
                        !checked.every(Boolean) ||
                        (!portal && detail.audienceCount === 0)
                      }
                      onClick={() => void submit()}
                    >
                      <Send size={15} />
                      {working ? "正在送审…" : `确认${config.submitLabel}`}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" disabled={working || uploading} onClick={close}>
                      关闭
                    </Button>
                    {canEdit && (
                      <Button
                        variant="outline"
                        disabled={working || uploading}
                        onClick={() => startEditor(detail)}
                      >
                        <SquarePen size={15} />
                        编辑草稿
                      </Button>
                    )}
                    {hasRole("CONTENT_AUTHOR") && detail.status === "DRAFT" && (
                      <Button
                        disabled={working || uploading}
                        onClick={() => {
                          setReviewing(true);
                          setChecked([false, false]);
                          setFailure(null);
                        }}
                      >
                        <Send size={15} />
                        核对并{config.submitLabel}
                      </Button>
                    )}
                  </>
                )}
              </footer>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Desk>
  );
}
